import 'assets/styles/app.scss';
import { MedalTabOptions, ProfileSection } from 'features/customerPanel/cpPlayerProfile/interface/interface';
import { EntertainmentTabOptions } from 'features/entertainment/interface';
import AutoBiometricLogin from 'features/login/component/AutoBiometricLogin';
import { PaymentTabType } from 'features/staticPages/container/interface';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch, useHistory, withRouter } from 'react-router-dom';
import ErrorPage from 'shared/components/404/404';
import AppUnavailable from 'shared/components/AppUnavailable/AppUnavailable';
import { LiveEventLoader } from 'shared/components/loader';
import Modal from 'shared/components/Modal';
import ScrollToTop from 'shared/components/scrollToTop';
import SessionExpired from 'shared/components/sessionExpired';
import StateWatcher from 'shared/components/stateWatcher';
import Notification from 'shared/components/toast';
import { APP_URL } from 'shared/constants/common';
import { environment, environmentSchema } from 'shared/constants/common-meta';
import Layout from 'shared/hoc/layout/layout';
import Orientation from 'shared/hoc/layout/orientation';
import { useDeviceManager } from 'shared/hooks/useDeviceManager';
import { useGlobalClickHandler } from 'shared/hooks/useGlobalClickHandler/useGlobalClickHandler.hook';
import usePwaPrompt from 'shared/hooks/usePwaPrompt';
import { State } from 'shared/interface';
import { getUserDetails, isAndroidNewApp, isIOSApp } from 'shared/util/utility';
import * as asyncComponent from './asyncComponent';
import { LoaderSlider } from './features/Banner/components/LoaderSlider';
import { LoaderSquare } from './features/Banner/components/LoaderSquare';
import InactiveUserDialog from './shared/components/dialog/inactiveUserDialog';
import WinningConfettiDialog from './shared/components/dialog/winningConfettiDialog';
import './shared/testing';

const App: React.FC<RouteComponentProps> = () => {
	const deviceManager = useDeviceManager();
	const history = useHistory();
	deviceManager.set();
	usePwaPrompt();
	useGlobalClickHandler();
	const errorPlaceholder = useSelector<State, boolean>((state) => state.home.errorPlaceholder);
	const verificationModalOpen = useSelector<State, boolean>((state) => state.login.verificationModalOpen);
	const localUserDetails = getUserDetails().login;
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false,
				retry: false,
			},
		},
	});
	if (errorPlaceholder) return <AppUnavailable />;

	try {
		environmentSchema.validateSync(environment);
	} catch (error: any) {
		console.error(error);
	}

	return (
		<>
			<StateWatcher />
			{localUserDetails === undefined && (isIOSApp || isAndroidNewApp) && <AutoBiometricLogin />}
			<Notification />
			<Orientation />
			<SessionExpired />
			<WinningConfettiDialog />
			<InactiveUserDialog open={verificationModalOpen} mode="LOGIN" />
			<QueryClientProvider client={queryClient}>
				<ErrorBoundary
					fallbackRender={(props) => {
						return <AppUnavailable canReload {...props} />;
					}}
				>
					<Modal />
					<Layout>
						<ScrollToTop />
						<Suspense
							fallback={
								<div>
									{history.location.pathname === APP_URL.home && (
										<>
											<LoaderSquare visible={true} />
											<LoaderSlider visible={true} />
										</>
									)}
									<LiveEventLoader />
								</div>
							}
						>
							<Switch>
								<Route exact path={APP_URL.home} component={asyncComponent.HomeWrapper} />
								<Route exact path={APP_URL.map} component={asyncComponent.Map} />
								<Route exact path={APP_URL.login} component={asyncComponent.Login} />
								<Route
									exact
									path={`${APP_URL.activateAccount}/:customerId/:activationCode/:lang`}
									component={asyncComponent.ActivateAccount}
								/>
								<Route exact path={APP_URL.regulations} component={asyncComponent.Regulations} />
								<Route exact path={APP_URL.register} component={asyncComponent.RegistrationMethod} />

								<Route exact path={APP_URL.verification} component={asyncComponent.VerificationMethod} />
								<Route exact path={`${APP_URL.registrationForm}/:promotionCode?`} component={asyncComponent.Registration} />
								<Route
									exact
									path={`${APP_URL.registrationForm}/:promotionCode?/:categoryName`}
									component={asyncComponent.Registration}
								/>
								<Route exact path={APP_URL.registrationMojeId} component={asyncComponent.RegistrationMojeId} />
								<Route exact path={APP_URL.verificationMojeId} component={asyncComponent.VerificationMojeIdAuthWrapper} />

								<Route exact path={APP_URL.forgotPassword} component={asyncComponent.ForgotPassword} />
								<Route exact path={`${APP_URL.resetPassword}/:customerId/:code/:lang`} component={asyncComponent.ForgotPassword} />
								<Route exact path={APP_URL.live} component={asyncComponent.Live} />

								<Route exact path={APP_URL.eSport} component={asyncComponent.ESSport} />
								<Redirect exact path={`${APP_URL.eSport}/:sportName`} to={APP_URL.eSport} />
								<Route exact path={`${APP_URL.eSport}/:sportName/:categoryName/:id`} component={asyncComponent.CategorySport} />
								<Route
									exact
									path={`${APP_URL.eSport}/:sportName/:categoryname/:league/:id`}
									component={asyncComponent.SecondCategorySport}
								/>
								<Route
									exact
									path={`${APP_URL.eSport}/:sportName/:parentName/:categoryName/:eventName/:id`}
									component={asyncComponent.PreMatch}
								/>
								<Route exact path={APP_URL.chatVip} component={asyncComponent.VipContactPage} />
								<Route exact path={APP_URL.promotion} component={asyncComponent.Promotion} />
								<Route exact path={APP_URL.privacy} component={asyncComponent.Privacy} />
								<Route exact path={APP_URL.contact} component={asyncComponent.Contact} />
								<Route exact path={APP_URL.deposit} component={asyncComponent.Deposit} />
								<Redirect exact path={APP_URL.payment} to={`${APP_URL.payment}/${PaymentTabType.PAYMENT}`} />
								<Route exact path={`${APP_URL.payment}/:tabType`} component={asyncComponent.Payment} />
								<Redirect exact path={APP_URL.entertainment} to={`${APP_URL.entertainment}/${EntertainmentTabOptions.ALL}`} />
								<Route exact path={`${APP_URL.entertainment}/:tabType`} component={asyncComponent.Entertainment} />
								<Route exact path={APP_URL.cardGames} component={asyncComponent.CardGames} />
								<Route exact path={APP_URL.virtualSports} component={asyncComponent.VirtualSports} />
								<Route exact path={APP_URL.pageNotFound}>
									<ErrorPage errorType="404" />
								</Route>
								<Route exact path={`${APP_URL.live}/:category/:participants/:id`} component={asyncComponent.EventLiveDetail} />
								{/* <Route exact path={APP_URL.chat} component={asyncComponent.Chat} /> */}
								<Route exact path={`${APP_URL.promotion}/:promotionId`} component={asyncComponent.PromotionDetails} />

								<Route exact path={`${APP_URL.coupon}`} component={asyncComponent.Coupons} />

								<Route exact path={APP_URL.eventLive} component={asyncComponent.EventLive} />
								<Route exact path={APP_URL.myBet} component={asyncComponent.MyBet} />

								<Route exact path={APP_URL.todaysOffer} component={asyncComponent.TodaysOffer} />
								<Route exact path={APP_URL.betMarking} component={asyncComponent.BetMarking} />

								<Route exact path={APP_URL.kuponKibica} component={asyncComponent.KuponKibica} />
								<Route exact path={APP_URL.shareKuponKibica} component={asyncComponent.ShareKuponKibica} />

								<Route exact path={APP_URL.sport} component={asyncComponent.Sport} />
								<Route exact path={`${APP_URL.sport}/:sportName`} component={asyncComponent.SportCategories} />
								<Route exact path={`${APP_URL.sport}/:sportName/:categoryName/:id`} component={asyncComponent.CategorySport} />
								<Route
									exact
									path={`${APP_URL.sport}/:sportName/:categoryname/:league/:id`}
									component={asyncComponent.SecondCategorySport}
								/>
								<Route
									exact
									path={`${APP_URL.sport}/:sportName/:parentName/:categoryName/:eventName/:id`}
									component={asyncComponent.PreMatch}
								/>
								<Route exact path={APP_URL.special} component={asyncComponent.Special} />
								<Route exact path={APP_URL.dailyChallenge} component={asyncComponent.DailyChallenge} />

								{/* <Route exact path={APP_URL.usefulLinks} component={asyncComponent.UsefulLinks} /> */}
								<Route exact path={`${APP_URL.root}:id`} component={asyncComponent.CustomPageDetails} />
								<Route exact path={APP_URL.betMatch} component={asyncComponent.BetMatch} />
								<Route exact path={APP_URL.playerProfile} component={asyncComponent.PlayerProfile} />
								<Route exact path={`${APP_URL.playerProfile}/${ProfileSection.MEDAL}`} component={asyncComponent.Medals} />
								<Route exact path={`${APP_URL.playerProfile}/${ProfileSection.MEDAL}/:tabType`} component={asyncComponent.Medals} />
								<Redirect
									exact
									from={`${APP_URL.playerProfile}/${ProfileSection.MEDAL}`}
									to={`${APP_URL.playerProfile}/${ProfileSection.MEDAL}/${MedalTabOptions.ACTIVE}`}
								/>

								<Redirect exact from={APP_URL.root} to={APP_URL.home} />
								<Redirect to={APP_URL.pageNotFound} />
							</Switch>
						</Suspense>
					</Layout>
				</ErrorBoundary>
			</QueryClientProvider>
		</>
	);
};
export default withRouter(App);
